import { getLegalEntity } from '@cemex/cmx-client-layout';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { getMultipleOrderOnlineState } from '../reducers';

import {
  selectActiveProductTypeEntity,
  selectProductTypeState,
} from 'app/shared/upload-document/redux/selectors/product-type.selector';
import { getActiveExperienceCode } from '@ordersnproducts/modules/order-builder/reducers';
import { DCOFeatureState } from '../../modules/dco/redux/reducers';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { validateTravelTimeData } from '@ordersnproducts/utils/common-utils';

export const getDCOMOO = createFeatureSelector<DCOFeatureState>('multipleOrderOnline');

export const selectActiveLegalEntityAndActiveProductType = createSelector(
  getLegalEntity,
  selectActiveProductTypeEntity,
  (activeLegalEntity, activeProductType) => ({
    activeLegalEntity,
    activeProductType,
  }),
);

export const selectActiveLegalEntityAndProductTypeState = createSelector(
  getLegalEntity,
  selectProductTypeState,
  (activeLegalEntity, productTypeState) => ({
    activeLegalEntity,
    productTypeState,
  }),
);

export const getShipmentLocationWorksheets = createSelector(
  getMultipleOrderOnlineState,
  (state) => state.shipmentLocationWorksheets,
);

export const getCalendarState = createSelector(
  getShipmentLocationWorksheets,
  (state) => state && state.calendarsResponse,
);

export const getActiveExperienceCodeAndCountryCode = createSelector(
  getActiveExperienceCode,
  getLegalEntity,
  (experienceCode, legalEntity) => {
    return {
      experienceCode,
      countryCode: legalEntity.countryCode,
    };
  },
);

export const getCalendarStateAndCountry = createSelector(
  getCalendarState,
  getLegalEntity,
  (calendarState, legalEntity) => {
    return { calendarState, legalEntity };
  },
);

export const getCutOffState = createSelector(getShipmentLocationWorksheets, (state) => {
  return state.cutOffResponse;
});

export const getFullSalesAreasState = createSelector(getShipmentLocationWorksheets, (state) => state.fullSalesAreas);

export const getOfferSlots = createSelector(getCalendarState, (state) => state.offerSlots);

export const getCutOffAndCalendarResponse = createSelector(
  getCalendarState,
  getCutOffState,
  (calendarState, cutOffState) => {
    if (calendarState && cutOffState) {
      return {
        calendarResponse: calendarState,
        cutOffResponse: cutOffState,
      };
    }
    return undefined;
  },
);
export const getCutOffAndOfferDays = createSelector(getCalendarState, getCutOffState, (calendarState, cutOffState) => {
  if (calendarState && cutOffState) {
    let offerDays: number;
    try {
      offerDays = calendarState.offerDays - 1;
    } catch (e) {
      offerDays = 29;
    }
    const time = cutOffState.timeCut.replace('.000Z', '');
    const cutOffString = cutOffState.dateAvailable + 'T' + time;
    return {
      cutOffString: cutOffString,
      offerDays: offerDays,
      businessDays: calendarState.businessDays || [],
      temperatureControlBusinessDays: calendarState.temperatureControlBusinessDays || [],
    };
  }
  return undefined;
});

export const getSelectedSlot = createSelector(getDCOMOO, (state) => {
  return state && state.slots && state.slots.selectedSlot;
});

/**
 * Get the selected slot and travel time and use calendar state as fallback
 */
export const getSelectedSlotAndTravelTime = createSelector(
  getSelectedSlot,
  getCalendarStateAndCountry,
  getDCOMOO,
  (slot, calendar, dco) => {
    const resultDCO = { slot };

    if (!slot) {
      // CXGOOLS-7585: when there is no selected slot but DCO returned proposals (e.g. the
      // requested time has no slot and the order goes to "In Review"/waiting list), the
      // proposals already carry the transit/distance toward the destination sent to DCO —
      // i.e. the selected Point of Delivery for various-locations orders. Prefer that
      // POD-based value over the jobsite-only calendar.shipment fallback. Defensive _safe:
      // if the proposal can't be read, it degrades to the previous calendar behavior.
      const dcoProposal = _safe(() => {
        const slotsState: any = dco.slots;
        const firstId = slotsState.ids[0];
        return slotsState.entities[firstId].response.slots[0].concreteProposals[0];
      });
      if (dcoProposal && (dcoProposal.travelDistance != null || dcoProposal.travelTimeToPlant != null)) {
        return {
          slot: {
            travelTimeToPlant: dcoProposal.travelTimeToPlant,
            travelTimeFromPlant: dcoProposal.travelTimeFromPlant,
            travelDistance: dcoProposal.travelDistance,
          },
        };
      }
      // send calendar to get the slot
      const tmp = {
        travelTimeToPlant: _safe(() => calendar.calendarState.shipment.travelTime),
        travelTimeFromPlant: _safe(() => calendar.calendarState.shipment.travelTime),
        travelDistance: _safe(() => calendar.calendarState.shipment.distance, 30),
      };
      const shipment = _safe(() => calendar.calendarState.shipment);
      // if shipment is valid send the calendar data otherwise send undefined
      const resultSlot = validateTravelTimeData(shipment) ? tmp : undefined;
      return { slot: resultSlot };
    }
    // send DCO slot
    return resultDCO;
  },
);
